import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import {
  Sparkles, Send, Trash2, ExternalLink, Loader2, CheckCircle2,
  Clock, Edit3, Save, X, AlertTriangle, Copy, ImageOff, ImageIcon,
  Twitter, Facebook, Instagram, MessageSquare, Youtube, Music2,
} from "lucide-react";
import api, { API } from "../lib/api";

const PLATFORMS = [
  { id: "x", label: "X / Twitter", Icon: Twitter, manual: false },
  { id: "reddit", label: "Reddit", Icon: MessageSquare, manual: false },
  { id: "instagram", label: "Instagram", Icon: Instagram, manual: false },
  { id: "facebook", label: "Facebook", Icon: Facebook, manual: false },
  { id: "tiktok", label: "TikTok", Icon: Music2, manual: true },
  { id: "youtube", label: "YouTube", Icon: Youtube, manual: true },
];

const STATUS_COLOR = {
  queued: { c: "#F7B500", label: "Pending review" },
  approved: { c: "#5EEAD4", label: "Approved" },
  posted: { c: "#A6FF00", label: "Posted" },
  failed: { c: "#EF4444", label: "Failed" },
  manual: { c: "#9CA8B8", label: "Manual paste" },
  cancelled: { c: "#9CA8B8", label: "Cancelled" },
};

function PlatformPill({ id }) {
  const p = PLATFORMS.find((x) => x.id === id) || { label: id };
  const I = p.Icon;
  return (
    <span className="inline-flex items-center gap-1.5 pg-mono text-[10px] uppercase tracking-[0.2em] text-[#5EEAD4]">
      {I ? <I size={11} /> : null}
      {p.label}
    </span>
  );
}

function StatusPill({ s }) {
  const v = STATUS_COLOR[s] || { c: "#9CA8B8", label: s };
  return (
    <span
      className="pg-mono text-[10px] uppercase tracking-[0.2em] px-2 py-0.5 rounded-sm border"
      style={{
        color: v.c,
        borderColor: `${v.c}55`,
        background: `${v.c}11`,
      }}
    >
      {v.label}
    </span>
  );
}

function PostRow({ post, configured, onChange }) {
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [caption, setCaption] = useState(post.caption);
  const [title, setTitle] = useState(post.title || "");
  const [pasteUrl, setPasteUrl] = useState("");
  const isManual = ["tiktok", "youtube"].includes(post.platform);

  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/admin/social/${post.id}`, { caption, title });
      toast.success("Saved");
      setEditing(false);
      onChange();
    } catch (e) { toast.error("Save failed"); }
    finally { setBusy(false); }
  };

  const setStatus = async (status) => {
    setBusy(true);
    try {
      await api.patch(`/admin/social/${post.id}`, { status });
      toast.success(status);
      onChange();
    } catch (e) { toast.error("Update failed"); }
    finally { setBusy(false); }
  };

  const publishNow = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/social/${post.id}/publish-now`);
      if (data.ok) toast.success("Published");
      else toast.error(data.publish_result?.error || "Publish failed");
      onChange();
    } catch (e) { toast.error("Publish failed"); }
    finally { setBusy(false); }
  };

  const markPosted = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/social/${post.id}/mark-posted`, { post_url: pasteUrl });
      toast.success("Marked as posted");
      onChange();
    } catch (e) { toast.error("Failed"); }
    finally { setBusy(false); }
  };

  const remove = async () => {
    if (!window.confirm("Delete this post permanently?")) return;
    setBusy(true);
    try {
      await api.delete(`/admin/social/${post.id}`);
      toast.success("Deleted");
      onChange();
    } catch (e) { toast.error("Delete failed"); }
    finally { setBusy(false); }
  };

  const copyCaption = async () => {
    try {
      await navigator.clipboard.writeText(post.caption);
      toast.success("Caption copied");
    } catch { toast.error("Could not copy"); }
  };

  const cardSrc = `${API}/social-cards/${post.id}.png`;
  const isConfigured = configured?.[post.platform];

  return (
    <div
      className="pg-card p-5 space-y-3"
      data-testid={`social-row-${post.id}`}
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3 flex-wrap">
          <PlatformPill id={post.platform} />
          <StatusPill s={post.status} />
          <span className="text-[11px] text-[#9CA8B8] pg-mono">
            <Clock size={10} className="inline mr-1" />
            {(post.created_at || "").slice(0, 16).replace("T", " ")}
          </span>
          {!isConfigured && !isManual && (
            <span className="text-[10px] text-[#F7B500] pg-mono uppercase tracking-wider inline-flex items-center gap-1">
              <AlertTriangle size={10} /> Needs API key
            </span>
          )}
          {isManual && (
            <span className="text-[10px] text-[#5EEAD4] pg-mono uppercase tracking-wider">
              Manual paste platform
            </span>
          )}
        </div>
        <div className="flex items-center gap-1">
          {!editing && (
            <button onClick={() => setEditing(true)} className="pg-btn-ghost text-[10px]" data-testid={`social-edit-${post.id}`}>
              <Edit3 size={11} /> Edit
            </button>
          )}
          <button onClick={remove} className="pg-btn-ghost text-[10px]" data-testid={`social-delete-${post.id}`}>
            <Trash2 size={11} />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-2">
          {post.platform === "reddit" && (
            editing ? (
              <input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="w-full bg-[#0B1220] border border-[#1E2E44] text-[#F7FAFC] rounded-sm px-3 py-2 text-sm"
                placeholder="Reddit title"
                data-testid={`social-title-input-${post.id}`}
              />
            ) : (
              <div className="text-[#F7FAFC] font-medium">{post.title || "(no title)"}</div>
            )
          )}
          {editing ? (
            <textarea
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              rows={6}
              className="w-full bg-[#0B1220] border border-[#1E2E44] text-[#F7FAFC] rounded-sm px-3 py-2 text-[13px] leading-relaxed font-normal"
              data-testid={`social-caption-input-${post.id}`}
            />
          ) : (
            <pre
              className="text-[12.5px] text-[#9CA8B8] whitespace-pre-wrap leading-relaxed font-normal m-0"
              style={{ fontFamily: "inherit" }}
              data-testid={`social-caption-${post.id}`}
            >
{post.caption}
            </pre>
          )}
          <div className="text-[10px] text-[#9CA8B8] pg-mono">
            {post.caption.length} chars · topic: {post.topic_id}
          </div>
        </div>
        <div className="space-y-2">
          <div className="bg-[#0B1220] border border-[#1E2E44] rounded-sm p-2 flex items-center justify-center min-h-[140px]">
            <img
              src={cardSrc}
              alt={post.alt_text || "Peptigen Labs branded share card"}
              className="max-w-full max-h-[200px] rounded-sm"
              onError={(e) => { e.currentTarget.style.display = "none"; }}
              data-testid={`social-card-img-${post.id}`}
            />
          </div>
          <div className="text-[10px] text-[#9CA8B8] pg-mono">
            {post.image_size} · alt: {post.alt_text?.slice(0, 30) || "—"}
          </div>
        </div>
      </div>

      {editing && (
        <div className="flex gap-2">
          <button onClick={save} disabled={busy} className="pg-btn-primary text-xs" data-testid={`social-save-${post.id}`}>
            {busy ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />} Save
          </button>
          <button onClick={() => { setEditing(false); setCaption(post.caption); setTitle(post.title || ""); }} className="pg-btn-ghost text-xs">
            <X size={12} /> Cancel
          </button>
        </div>
      )}

      {!editing && (
        <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-[#1E2E44]">
          {post.status === "queued" && (
            <button onClick={() => setStatus("approved")} disabled={busy} className="pg-btn-ghost text-xs" data-testid={`social-approve-${post.id}`}>
              <CheckCircle2 size={12} /> Approve
            </button>
          )}
          {post.status === "queued" && (
            <button onClick={() => setStatus("cancelled")} disabled={busy} className="pg-btn-ghost text-xs">
              <X size={12} /> Reject
            </button>
          )}
          {!isManual && post.status !== "posted" && isConfigured && (
            <button onClick={publishNow} disabled={busy} className="pg-btn-primary text-xs" data-testid={`social-publish-now-${post.id}`}>
              {busy ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />} Publish now
            </button>
          )}
          {(isManual || post.status === "failed") && (
            <button onClick={copyCaption} className="pg-btn-ghost text-xs" data-testid={`social-copy-${post.id}`}>
              <Copy size={12} /> Copy caption
            </button>
          )}
          {(isManual || post.status === "failed") && (
            <a href={cardSrc} target="_blank" rel="noreferrer" className="pg-btn-ghost text-xs" data-testid={`social-download-img-${post.id}`}>
              <ImageIcon size={12} /> Open image
            </a>
          )}
          {(isManual && post.status !== "posted") && (
            <div className="flex items-center gap-2 ml-auto flex-wrap">
              <input
                type="url"
                placeholder="Paste live URL after posting"
                value={pasteUrl}
                onChange={(e) => setPasteUrl(e.target.value)}
                className="bg-[#0B1220] border border-[#1E2E44] text-[#F7FAFC] rounded-sm px-2 py-1 text-xs min-w-[180px]"
                data-testid={`social-paste-url-${post.id}`}
              />
              <button onClick={markPosted} disabled={busy} className="pg-btn-ghost text-xs" data-testid={`social-mark-posted-${post.id}`}>
                <CheckCircle2 size={12} /> Mark posted
              </button>
            </div>
          )}
          {post.post_url && (
            <a href={post.post_url} target="_blank" rel="noreferrer" className="pg-btn-ghost text-xs ml-auto" data-testid={`social-open-live-${post.id}`}>
              <ExternalLink size={12} /> Open live post
            </a>
          )}
          {post.error && (
            <div className="text-[11px] text-[#EF4444] mt-1 w-full">
              <AlertTriangle size={11} className="inline mr-1" /> {post.error}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function AdminSocial() {
  const [data, setData] = useState({ posts: [], configured: {}, manual_platforms: [] });
  const [filter, setFilter] = useState({ platform: "", status: "" });
  const [drafting, setDrafting] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filter.platform) params.set("platform", filter.platform);
      if (filter.status) params.set("status", filter.status);
      const { data } = await api.get(`/admin/social${params.toString() ? `?${params}` : ""}`);
      setData(data);
    } catch { toast.error("Could not load social posts"); }
    finally { setLoading(false); }
  }, [filter.platform, filter.status]);

  useEffect(() => { load(); }, [load]);

  const draft = async (platform) => {
    setDrafting(platform);
    try {
      await api.post("/admin/social/draft-now", { platform });
      toast.success(`Drafted ${platform}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Draft failed"); }
    finally { setDrafting(null); }
  };

  const queued = data.posts.filter((p) => p.status === "queued").length;
  const posted24h = data.posts.filter((p) => p.status === "posted" && p.posted_at &&
    Date.now() - new Date(p.posted_at).getTime() < 86_400_000).length;

  return (
    <div data-testid="admin-social-page">
      <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
        <div>
          <div className="pg-micro mb-2">// Admin · Social media agent</div>
          <h1 className="text-[#F7FAFC] text-3xl pg-display" style={{ fontFamily: "'Exo 2'" }}>
            Posts & queue
          </h1>
          <p className="text-[#9CA8B8] text-sm mt-1 max-w-2xl">
            Twice-daily AI drafts per platform. Review, edit, approve — then we publish via the
            platform's official API. TikTok / YouTube have no public API: we render the caption +
            image and you copy-paste into the app, then mark it posted with the live URL.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <div className="pg-card p-5">
          <div className="pg-micro mb-2">// Pending review</div>
          <div className="text-3xl pg-heading text-[#F7B500]" style={{ fontFamily: "'Rajdhani'" }}>{queued}</div>
        </div>
        <div className="pg-card p-5">
          <div className="pg-micro mb-2">// Posted in last 24 h</div>
          <div className="text-3xl pg-heading text-[#A6FF00]" style={{ fontFamily: "'Rajdhani'" }}>{posted24h}</div>
        </div>
        <div className="pg-card p-5">
          <div className="pg-micro mb-2">// Schedule</div>
          <div className="text-base pg-heading text-[#5EEAD4]" style={{ fontFamily: "'Rajdhani'" }}>
            Twice daily · ~9 h gap
          </div>
        </div>
      </div>

      <div className="pg-card p-4 mb-6 flex items-center gap-2 flex-wrap">
        <span className="pg-micro mr-1">// Draft now:</span>
        {PLATFORMS.map((p) => {
          const I = p.Icon;
          const isOn = data.configured?.[p.id] || p.manual;
          return (
            <button
              key={p.id}
              onClick={() => draft(p.id)}
              disabled={drafting === p.id}
              className="pg-btn-ghost text-xs"
              data-testid={`social-draft-${p.id}`}
              style={{
                opacity: isOn ? 1 : 0.7,
                borderColor: isOn ? "#1E2E44" : "rgba(247,181,0,0.4)",
              }}
              title={isOn ? "" : "Add API keys in backend/.env (see SOCIAL_SETUP.md)"}
            >
              {drafting === p.id ? <Loader2 size={12} className="animate-spin" /> : <I size={12} />}
              {p.label}{!isOn && !p.manual ? " · ⚠️" : ""}
            </button>
          );
        })}
      </div>

      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <span className="pg-micro mr-1">// Filter:</span>
        <select
          value={filter.platform}
          onChange={(e) => setFilter({ ...filter, platform: e.target.value })}
          className="bg-[#0B1220] border border-[#1E2E44] text-[#F7FAFC] rounded-sm px-2 py-1 text-xs"
          data-testid="social-filter-platform"
        >
          <option value="">All platforms</option>
          {PLATFORMS.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
        </select>
        <select
          value={filter.status}
          onChange={(e) => setFilter({ ...filter, status: e.target.value })}
          className="bg-[#0B1220] border border-[#1E2E44] text-[#F7FAFC] rounded-sm px-2 py-1 text-xs"
          data-testid="social-filter-status"
        >
          <option value="">All statuses</option>
          {Object.keys(STATUS_COLOR).map((s) => <option key={s} value={s}>{STATUS_COLOR[s].label}</option>)}
        </select>
      </div>

      {loading ? (
        <div className="pg-card p-10 text-center text-[#9CA8B8] text-sm">
          <Loader2 size={18} className="animate-spin text-[#00C2D6] mx-auto mb-3" /> Loading…
        </div>
      ) : data.posts.length === 0 ? (
        <div className="pg-card p-10 text-center text-[#9CA8B8] text-sm">
          <ImageOff size={18} className="text-[#00C2D6] mx-auto mb-3" />
          No posts yet. Use a "Draft now" button above to create your first one.
        </div>
      ) : (
        <div className="space-y-4">
          {data.posts.map((p) => (
            <PostRow key={p.id} post={p} configured={data.configured} onChange={load} />
          ))}
        </div>
      )}
    </div>
  );
}
